import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { Cropper } from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import { Tooltip } from "react-tooltip";
import { COLORS } from '../../colorTheme/colorTheme';
import domainName from '../../domainName';


const EditPostModal = ({ show, onClose, editItem, isFeed, t, editCommunityNews }) => {

    const [messageValue, setMessageValue] = useState(editItem?.title || '');
    const [selectedPicture, setSelectedPicture] = useState(null);
    const [previewImage, setPreviewImage] = useState(editItem?.ListingImageDisplay[0]);
    const [listingImage, setListingImage] = useState(editItem?.listingImage);
    const [cropModalVisible, setCropModalVisible] = useState(false);
    const [selectedPictureFile, setSelectedPictureFile] = useState(null);
    const cropperRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [isNewImage, setIsNewImage] = useState(false);

    const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
    const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg"];

    const handlePictureChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            alert(t("errorPush.Invalid file type") + "\n" + t("errorPush.Please upload a valid image."));
            return;
        }

        if (file.size > MAX_IMAGE_SIZE) {
            alert(t("errorPush.File too large") + "\n" + t("errorPush.File size must be less than") + " 10MB.");
            return;
        }

        setSelectedPictureFile(file);
        setCropModalVisible(true);
    };

    const uploadFile = async (file) => {
        try {
            // Extract the file extension and MIME type
            const fileExtension = file.type.split('/').pop();
            const mimeType = file.type;

            // Create a FormData object
            const formData = new FormData();
            formData.append('files', file); // Append the file
            formData.append('FolderName', 'community_post'); // Specify the folder name

            // Send the request to the backend API
            const response = await domainName.sendBackendUploadImage(
                `${domainName.debugAPI}/service/commonUploadS3`,
                formData
            );

            if (response && response.ReturnCode === 200 && response.Data.length) {
                // Return the uploaded file path
                return response.Data[0];
            } else {
                throw new Error('Failed to upload file');
            }
        } catch (error) {
            console.error('Error in uploadFile:', error);
            return null;
        }
    };

    const cropImage = () => {
        if (cropperRef.current && cropperRef.current.cropper.getCroppedCanvas) {
            const croppedCanvas = cropperRef.current.cropper.getCroppedCanvas({ width: 800, height: 800 });

            if (croppedCanvas) {
                croppedCanvas.toBlob((blob) => {
                    const croppedFile = new File([blob], `cropped_${Date.now()}.jpg`, { type: "image/jpeg" });

                    setSelectedPicture(croppedFile);
                    setPreviewImage(URL.createObjectURL(croppedFile));
                    setCropModalVisible(false);
                    setSelectedPictureFile(null);
                    setIsNewImage(true);
                }, "image/jpeg");
            }
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        if (!messageValue.trim()) {
            alert(t("error.Text content field cannot be empty."));
            setLoading(false);
            return;
        }

        if (isNewImage && selectedPicture) {
            const imageFromS3 = await uploadFile(selectedPicture);
            // console.log("editItem._id", editItem._id)
            // console.log("imageFromS3", imageFromS3)
            // console.log("messageValue", messageValue)
            await editCommunityNews(editItem._id, [imageFromS3], messageValue);
        } else if (!previewImage) {
            // console.log("editItem._id", editItem._id)
            // console.log("previewImage", previewImage)
            // console.log("messageValue", messageValue)
            await editCommunityNews(editItem._id, [], messageValue);
        } else {
            // console.log("editItem._id", editItem._id)
            // console.log("previewImage", listingImage)
            // console.log("messageValue", messageValue)
            await editCommunityNews(editItem._id, [listingImage], messageValue);
        }

        onClose();
        setLoading(false);
    };

    useEffect(() => {
        if (show && editItem) {
            setMessageValue(editItem.title || '');
            setPreviewImage(editItem.ListingImageDisplay?.[0] || null);
            setListingImage(editItem.listingImage || null);
            setSelectedPicture(null);
        }
    }, [show, editItem]);

    return (
        <Modal show={show} onHide={onClose} fade={false}>
            <Modal.Header closeButton>
                <Modal.Title>{t('community.Edit')} {isFeed ? t('community.Feed') : t('community.News')} {t('community.Post')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <textarea
                    style={{
                        height: '120px', borderColor: COLORS.lightGray1, borderWidth: 1, borderRadius: 5,
                        width: '100%', boxSizing: 'border-box', padding: '5px', fontSize: '16px', resize: 'none'
                    }}
                    placeholder={t("community.Share your thoughts")}
                    value={messageValue}
                    onChange={(e) => setMessageValue(e.target.value)}
                />
                <div style={{ paddingTop: "2%", paddingBottom: "2%", display: "flex", alignItems: "center" }}>
                    <div
                        data-tooltip-id="imageUploadTooltip"
                        data-tooltip-html={`
                            <div style="text-align: left; padding: 5px; font-weight: bold;">
                                <div>${t("serviceManagement.Tooltip title")}</div>
                                <div>${t("serviceManagement.Tooltip Content 1")} 10MB</div>
                                <div>${t("serviceManagement.Tooltip Content 3")}</div>
                                <div>${t("serviceManagement.Tooltip Content 8")}</div>
                            </div>
                        `}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                        }}
                    >
                        <label
                            htmlFor="imageUpload"
                            style={{
                                cursor: 'pointer',
                                color: COLORS.orange,
                                textDecoration: 'underline',
                            }}
                        >
                            {t('community.Choose an image')}
                        </label>
                        <input
                            type="file"
                            id="imageUpload"
                            accept=".png, .jpeg, .jpg"
                            onChange={handlePictureChange}
                            style={{ display: 'none' }}
                        />
                        <span
                            style={{
                                display: "inline-flex",
                                justifyContent: "center",
                                alignItems: "center",
                                width: "20px",
                                height: "20px",
                                marginLeft: "10px",
                                backgroundColor: "white",
                                border: "1px solid black",
                                borderRadius: "50%",
                            }}
                        >
                            <div
                                style={{
                                    width: "18px",
                                    height: "18px",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    border: "1px solid black",
                                    borderRadius: "50%",
                                    backgroundColor: COLORS.white,
                                    color: COLORS.black,
                                    fontSize: "12px",
                                    fontWeight: "bold"
                                }}
                            >
                                i
                            </div>
                        </span>
                    </div>

                    <Tooltip
                        id="imageUploadTooltip"
                        place="right"
                        style={{
                            backgroundColor: COLORS.white,
                            color: COLORS.black,
                            border: "1px solid white",
                            borderRadius: "5px",
                            boxShadow: "0px 6px 12px rgba(0, 0, 0, 0.2)",
                            padding: "5px",
                            zIndex: 100,
                        }}
                    />
                </div>

                {previewImage && (
                    <div style={{ position: 'relative', marginTop: '3%', width: '150px' }}>
                        <img src={previewImage} width={150} height={150} style={{ borderRadius: '10px' }} alt="Preview" />
                        <div onClick={() => { setSelectedPicture(null); setPreviewImage(null); }} style={{ position: 'absolute', top: '2px', right: '2px', zIndex: '1' }}>
                            <img src={require('../../img/icons/close.png')} style={{ width: '20px', height: '20px', backgroundColor: 'white', borderRadius: '10px', cursor: 'pointer' }} alt="Close" />
                        </div>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>
                    {t('community.Cancel')}
                </Button>
                <Button variant="primary" onClick={handleSubmit} disabled={loading}>
                    {loading ? t('community.Loading') : t('community.Confirm')}
                </Button>
            </Modal.Footer>

            <Modal show={cropModalVisible} onHide={() => setCropModalVisible(false)} centered>
                {selectedPictureFile && (
                    <Cropper
                        style={{ height: 400, width: "100%" }}
                        aspectRatio={1}
                        preview=".img-preview"
                        guides={false}
                        src={URL.createObjectURL(selectedPictureFile)}
                        ref={cropperRef}
                        viewMode={1}
                    />
                )}
                <button onClick={cropImage} style={{ width: "100%", color: 'white', padding: 10, backgroundColor: '#C0281C', border: 0, borderRadius: 10 }}>
                    Crop Image
                </button>
            </Modal>
        </Modal>
    );
};

export default EditPostModal;
